import { useState } from 'react'

interface ValveCreationModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (data: { name: string; description: string; status: string }) => void
  coordinates: [number, number] | null // [lng, lat]
}

export default function ValveCreationModal({ isOpen, onClose, onSave, coordinates }: ValveCreationModalProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState<'open' | 'closed'>('closed')

  if (!isOpen) return null

  const handleSave = () => {
    if (!name.trim()) return
    onSave({ name: name.trim(), description: description.trim(), status })
    // Reset form
    setName('')
    setDescription('')
    setStatus('closed')
  }

  const handleClose = () => {
    setName('')
    setDescription('')
    setStatus('closed')
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-stretch justify-center z-[9999] sm:items-center sm:p-4" onClick={handleClose}>
      <div className="bg-white sm:rounded-xl shadow-2xl w-full sm:max-w-md flex flex-col max-h-full sm:max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-gray-200 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">🔴 Nowa zasuwa sieciowa</h2>
          <button onClick={handleClose} className="p-1 -mr-1 text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-6 py-4 space-y-4">
          {/* Coordinates display */}
          {coordinates && (
            <div className="bg-gray-50 rounded-lg px-4 py-2 text-sm text-gray-600">
              📍 Lokalizacja: {coordinates[1].toFixed(6)}, {coordinates[0].toFixed(6)}
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nazwa zasuwy <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="np. Zasuwa DN100 ul. Główna"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Opis (opcjonalnie)
            </label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Dodatkowe informacje o zasuwie..."
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Stan początkowy
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setStatus('closed')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors ${
                  status === 'closed'
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                🔴 Zamknięta
              </button>
              <button
                type="button"
                onClick={() => setStatus('open')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors ${
                  status === 'open'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                🟢 Otwarta
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 px-5 sm:px-6 py-4 border-t border-gray-200 shrink-0">
          <button
            onClick={handleClose}
            className="min-h-[44px] px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-lg transition-colors"
          >
            Anuluj
          </button>
          <button
            onClick={handleSave}
            disabled={!name.trim()}
            className="min-h-[44px] px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Utwórz zasuwę
          </button>
        </div>
      </div>
    </div>
  )
}
